/**
 * ObservabilityProvider.tsx
 *
 * Optional React context provider that makes the observability config
 * available to child components.  Applications call `initObservability`
 * BEFORE rendering this provider so Sentry is fully initialised at mount.
 *
 * Usage:
 *   <ObservabilityProvider app="platform" environment="production" release={gitSha}>
 *     <App />
 *   </ObservabilityProvider>
 */

import { createContext, type ReactNode, useContext } from 'react';
import type { AppName } from '../sentry.js';

export type ObservabilityContextValue = {
  app: AppName;
  environment: string;
  release: string;
};

const ObservabilityContext = createContext<ObservabilityContextValue | null>(null);

export type ObservabilityProviderProps = {
  app: AppName;
  environment: string;
  release: string;
  children: ReactNode;
};

export function ObservabilityProvider({
  app,
  environment,
  release,
  children,
}: ObservabilityProviderProps): ReactNode {
  return (
    <ObservabilityContext.Provider value={{ app, environment, release }}>
      {children}
    </ObservabilityContext.Provider>
  );
}

/**
 * Returns the current observability context.
 * Throws when called outside an `<ObservabilityProvider>`.
 */
export function useObservability(): ObservabilityContextValue {
  const ctx = useContext(ObservabilityContext);
  if (!ctx) {
    throw new Error('useObservability must be used within <ObservabilityProvider>');
  }
  return ctx;
}
